import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Loader from '../../../../component/common/Loader';
import SearchInput from '../../../../component/common/Input/SearchInput';
import Table from '../../../../component/common/Table';
import Pagination from '../../../../component/common/table2/Pagination';
import { fetchCompanyMoodUsers } from './companyMoodApi';
import { TableCard } from '../../../../component/common/designSystem';

const PER_PAGE = 10;

/**
 * Searchable users table for the Mood Tracking section.
 *
 * Volume and band mix only — how many check-ins, over how many days, what share
 * of them were positive, and when they last logged. Nothing a person wrote in a
 * check-in note, and no photo, appears here or on the profile behind it.
 *
 * Every user of the population is listed, including those who have never logged
 * a mood — they show dashes rather than zeros, so "hasn't started" reads
 * differently from "logged once and stopped".
 */
const MoodUsersTable = ({
  fetcher = fetchCompanyMoodUsers,
  onOpenUser,
  tableName,
  showCompanyColumn = false,
  department = ''
}) => {
  const [loader, setLoader] = useState(false);
  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');

  const fetchData = useCallback(
    async (page, search) => {
      setLoader(true);
      try {
        const response = await fetcher({ page, perPage: PER_PAGE, search, department });
        const mapped = (response.data || []).map((row) => {
          const logged = row.checkIns > 0;
          return {
            ...row,
            checkInsText: logged ? row.checkIns.toLocaleString() : '-',
            daysText: logged ? row.loggedDays.toLocaleString() : '-',
            positiveText: logged ? `${row.positiveRate}%` : '-',
            difficultText: logged ? `${row.difficultRate}%` : '-',
            lastCheckInDate: row.lastCheckInDate || '-'
          };
        });
        setRows(mapped);
        setTotalCount(response.totalCount || 0);
      } catch (error) {
        console.error('MoodUsersTable fetchData error:', error);
        setRows([]);
        setTotalCount(0);
      } finally {
        setLoader(false);
      }
    },
    [fetcher, department]
  );

  // Debounced fetch on search / page / scope change.
  useEffect(() => {
    const timeout = setTimeout(() => {
      fetchData(currentPage, searchTerm);
    }, 200);
    return () => clearTimeout(timeout);
  }, [fetchData, searchTerm, currentPage]);

  const columns = [
    { title: 'Name', key: 'user_profile', sortable: false, align: 'left', type: 'profile' },
    ...(showCompanyColumn
      ? [{ title: 'Company', key: 'company', sortable: false, align: 'center', type: 'text3' }]
      : []),
    { title: 'Check-ins', key: 'checkInsText', sortable: false, align: 'center', type: 'text3' },
    { title: 'Days Logged', key: 'daysText', sortable: false, align: 'center', type: 'text3' },
    { title: 'Positive', key: 'positiveText', sortable: false, align: 'center', type: 'text3' },
    { title: 'Difficult', key: 'difficultText', sortable: false, align: 'center', type: 'text3' },
    {
      title: 'Last Check-in',
      key: 'lastCheckInDate',
      sortable: false,
      align: 'center',
      type: 'text3'
    },
    { title: '', key: 'action', align: 'right', type: 'action', arrowClickFun: onOpenUser }
  ];

  return (
    <div>
      {loader ? <Loader /> : null}

      <div className="flex justify-end">
        <SearchInput
          id={`${tableName}-search`}
          name={`${tableName}-search`}
          type="text"
          value={searchTerm}
          onChange={(e) => {
            setCurrentPage(1);
            setSearchTerm(e.target.value);
          }}
          placeholder="Search users"
          className="w-full sm:w-auto px-3 py-2"
        />
      </div>

      <TableCard
          pagination={{
            currentPage: currentPage,
            totalCount: totalCount,
            pageSize: PER_PAGE,
            onPageChange: (page) => setCurrentPage(page)
          }}
        >
          <Table
          columns={columns}
          data={rows}
          name={tableName}
          bottomBorder={totalCount > PER_PAGE}
          loader={loader}
        />
        </TableCard>

      
    </div>
  );
};

MoodUsersTable.propTypes = {
  fetcher: PropTypes.func,
  onOpenUser: PropTypes.func,
  tableName: PropTypes.string,
  showCompanyColumn: PropTypes.bool,
  department: PropTypes.string
};

export default MoodUsersTable;
